import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash/fp";

import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import * as Events from "@app/common/workspaces/events";
import type {
  EnterpriseWorkspace,
  EnterpriseWorkspaceSettings,
  UpdateWorkspaceSettingsBody,
} from "@app/common/workspaces/types";
import { useAdminWorkspace } from "@app/common/workspaces/useWorkspace";

// generic key so that invalidating queries invalidates the workspace
// query as well as any workspace team queries
const WORKSPACE_INVALIDATE_QUERY_KEY = ["workspace"];

const useWorkspaceSettings = () => {
  const { fetchAndSetUser } = useAuth();
  const { workspace, workspaceLoading } = useAdminWorkspace();
  const queryClient = useQueryClient();

  const workspaceQueryKey = ["workspace", workspace.id];

  const updateWorkspaceSettingMutation = useMutation({
    mutationFn: async (body: UpdateWorkspaceSettingsBody) => {
      if (!workspace?.id) return Promise.reject();
      return apiClient.workspace.updateWorkspaceSettings(workspace.id, body);
    },
    onMutate: async (settingsToUpdate) => {
      await queryClient.cancelQueries({
        queryKey: WORKSPACE_INVALIDATE_QUERY_KEY,
      });
      const previousSettings =
        workspace.settings as EnterpriseWorkspaceSettings;

      if (isEmpty(previousSettings)) return;

      const newQueryData: EnterpriseWorkspace = {
        ...workspace,
        settings: {
          ...previousSettings,
          ...settingsToUpdate,
        },
      };

      queryClient.setQueryData(workspaceQueryKey, newQueryData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: WORKSPACE_INVALIDATE_QUERY_KEY,
      });
    },
    onSuccess: (data, settings) => {
      Events.changeWorkspaceSettings(settings);
      queryClient.invalidateQueries({
        queryKey: ["integrations"],
      });
      // refetch the user settings since a workspace setting update more likely
      // than not changes the user's access permissions
      fetchAndSetUser();
    },
    onError: (error) => Sentry.captureException(error),
  });

  return {
    isLoading: workspaceLoading,
    settings: workspace?.settings,
    updateWorkspaceSettings: updateWorkspaceSettingMutation,
  };
};

export default useWorkspaceSettings;
